import { supabase } from '../../lib/supabase';
import type { Idea } from '../../types/index';

export interface SavedIdeaWithRelations {
  id: string;
  saved_at: string | null;
  idea: {
    id: string;
    title: string | null;
    short_description: string | null;
    business_area: string | null;
    market_size: number | null;
    episode_id: string;
    episode?: {
      id: string;
      title: string | null;
      podcast?: {
        id: string;
        title: string | null;
      };
    };
  };
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * Get current user's saved ideas (RLS filters by user_id).
 * Supports pagination to match existing API shape.
 */
export async function getSavedIdeas(
  page: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<{ items: Idea[]; total_pages: number }> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();

  if (authError || !user) {
    throw new Error('User not authenticated');
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await supabase
    .from('saved_ideas')
    .select(
      `
      id,
      saved_at,
      idea:ideas (
        id,
        title,
        short_description,
        business_area,
        market_size,
        episode_id,
        episode:episodes (
          id,
          title,
          podcast:podcasts (
            id,
            title
          )
        )
      )
    `,
      { count: 'exact' }
    )
    .order('saved_at', { ascending: false })
    .range(from, to);

  if (error) {
    throw new Error(`Failed to fetch saved ideas: ${error.message}`);
  }

  const saved = (data ?? []) as SavedIdeaWithRelations[];
  const items = savedIdeasToIdeas(saved);
  const total = count ?? 0;
  const total_pages = Math.max(1, Math.ceil(total / pageSize));

  return { items, total_pages };
}

/**
 * Map saved-ideas response to Idea[] for SavedIdeasPage (flat list).
 */
export function savedIdeasToIdeas(saved: SavedIdeaWithRelations[]): Idea[] {
  return saved.map((s) => {
    const idea = s.idea as {
      id: string;
      title: string | null;
      short_description: string | null;
      business_area: string | null;
      market_size: number | null;
      episode_id: string;
      episode?: { id: string; title: string | null; podcast?: { id: string; title: string | null } };
    };
    return {
      id: idea.id,
      title: idea.title ?? '',
      content: idea.short_description ?? '',
      short_description: idea.short_description ?? '',
      detailed_description: '',
      market_size: idea.market_size ?? 0,
      business_area: idea.business_area ?? '',
      episode_id: idea.episode_id,
      upvotes: 0,
      downvotes: 0,
      created_at: (s as { saved_at?: string }).saved_at ?? '',
      updated_at: (s as { saved_at?: string }).saved_at ?? '',
    };
  });
}

/**
 * Save an idea for the current user.
 */
export async function saveIdea(ideaId: string): Promise<void> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();

  if (authError || !user) {
    throw new Error('User not authenticated');
  }

  const { error } = await supabase.from('saved_ideas').insert({
    user_id: user.id,
    idea_id: ideaId,
  });

  if (error) {
    if (error.code === '23505') {
      throw new Error('Idea already saved');
    }
    throw new Error(`Failed to save idea: ${error.message}`);
  }
}

/**
 * Unsave an idea for the current user.
 */
export async function unsaveIdea(ideaId: string): Promise<void> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();

  if (authError || !user) {
    throw new Error('User not authenticated');
  }

  const { error } = await supabase
    .from('saved_ideas')
    .delete()
    .eq('user_id', user.id)
    .eq('idea_id', ideaId);

  if (error) {
    throw new Error(`Failed to unsave idea: ${error.message}`);
  }
}

/**
 * Check if the current user has saved an idea.
 */
export async function isIdeaSaved(ideaId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { count, error } = await supabase
    .from('saved_ideas')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', user.id)
    .eq('idea_id', ideaId);

  if (error) {
    console.error('Error checking saved status:', error);
    return false;
  }

  return (count ?? 0) > 0;
}
